/**
 * SourceCircle — Individual verification circle for InputsPill
 *
 * Three visual states: idle → loading → resolved
 * Shows spinner ring during loading, checkmark + count badge when resolved.
 */

const CIRCLE_SIZE = 38;

export type CircleState = 'idle' | 'loading' | 'resolved';

export type InputSource = 'bank' | 'setup' | 'manual' | 'derived';

export const SOURCE_CONFIG: Record<InputSource, { icon: string; label: string; color: string }> = {
  bank:    { icon: 'B', label: 'Bank',    color: '#3DABA5' },
  setup:   { icon: 'S', label: 'Setup',   color: '#E07A3D' },
  manual:  { icon: 'M', label: 'Manual',  color: '#6366F1' },
  derived: { icon: 'D', label: 'Derived', color: '#94A3B8' },
};

interface SourceCircleProps {
  source: InputSource;
  state: CircleState;
  count: number;
  activeFilter: InputSource | null;
  onTap: () => void;
}

export function SourceCircle({ source, state, count, activeFilter, onTap }: SourceCircleProps) {
  const cfg = SOURCE_CONFIG[source];
  const isFiltered = activeFilter === source;

  const bg =
    state === 'idle' ? '#EEEDEB' :
    state === 'loading' ? `${cfg.color}10` :
    cfg.color;

  const contentColor =
    state === 'idle' ? '#C8C4BF' :
    state === 'loading' ? cfg.color :
    'white';

  const labelColor =
    state === 'resolved' ? cfg.color :
    state === 'loading' ? cfg.color :
    '#C8C4BF';

  return (
    <button
      onClick={onTap}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        background: 'none', border: 'none', cursor: state === 'resolved' ? 'pointer' : 'default',
        padding: 0, position: 'relative',
      }}
    >
      <div style={{
        width: CIRCLE_SIZE, height: CIRCLE_SIZE, borderRadius: '50%',
        backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', transition: 'background-color 0.28s ease, transform 0.2s ease',
        border: isFiltered ? `2.5px solid ${cfg.color}` : '2.5px solid transparent',
        transform: isFiltered ? 'scale(1.1)' : 'scale(1)',
      }}>
        {/* Letter or checkmark */}
        {state === 'resolved' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3.5 8.5L6.5 11.5L12.5 5.5"
              stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{
                strokeDasharray: 14,
                strokeDashoffset: 14,
                animation: 'srcCheck 0.3s ease forwards',
              }}
            />
          </svg>
        ) : (
          <span style={{
            fontSize: 14, fontWeight: 700, color: contentColor,
            fontFamily: "'Manrope', 'Inter', -apple-system, sans-serif",
          }}>{cfg.icon}</span>
        )}

        {/* Loading spinner ring */}
        {state === 'loading' && (
          <svg
            width={CIRCLE_SIZE + 4} height={CIRCLE_SIZE + 4}
            viewBox={`0 0 ${CIRCLE_SIZE + 4} ${CIRCLE_SIZE + 4}`}
            style={{
              position: 'absolute', top: -2, left: -2,
              animation: 'srcRing 0.7s linear infinite',
            }}
          >
            <circle
              cx={(CIRCLE_SIZE + 4) / 2} cy={(CIRCLE_SIZE + 4) / 2} r={CIRCLE_SIZE / 2}
              fill="none" stroke={cfg.color} strokeWidth="2"
              strokeDasharray={`${Math.PI * CIRCLE_SIZE * 0.3} ${Math.PI * CIRCLE_SIZE * 0.7}`}
              strokeLinecap="round"
            />
          </svg>
        )}

        {/* Count badge */}
        {state === 'resolved' && count > 0 && (
          <div style={{
            position: 'absolute', top: -3, right: -3,
            width: 17, height: 17, borderRadius: '50%',
            backgroundColor: 'white', border: `1.5px solid ${cfg.color}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 700, color: cfg.color,
            fontFamily: "'Manrope', 'Inter', -apple-system, sans-serif",
            animation: 'popIn 0.3s ease both',
          }}>{count}</div>
        )}
      </div>

      {/* Label */}
      <span style={{
        fontSize: 9, fontWeight: 600, color: labelColor,
        fontFamily: "'Manrope', 'Inter', -apple-system, sans-serif",
        transition: 'color 0.28s ease',
      }}>{cfg.label}</span>
    </button>
  );
}
